import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import {
  PmCard, PmPill, PmViewToggle, PmHeadCell, PmColumnPicker, useTableSortFilter, handleInlineEditKeyDown, type SortFilterColumn,
} from '../../../components/pm';
import {
  Box, Typography, Button, IconButton, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Stack, LinearProgress,
  Dialog, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Autocomplete, Checkbox, FormControlLabel, Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TableRowsIcon from '@mui/icons-material/TableRows';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HubIcon from '@mui/icons-material/Hub';
import SchemaIcon from '@mui/icons-material/Schema';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/de';
import { useTranslation } from 'react-i18next';
import {
  useArbeitspakete, useCreateArbeitspaket, useUpdateArbeitspaket, useDeleteArbeitspaket,
  useDeletedArbeitspakete, useRestoreArbeitspaket,
} from '../../../api/hooks/useArbeitspakete';
import { useSyncMaturityFromCount } from '../../../api/hooks/useReifegrad';
import { useProjectTeam } from '../../../api/hooks/useProjectTeam';
import { useNotifications } from '../../../context/NotificationContext';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { ApDependencyEditor } from './ApDependencyEditor';
import { ApMindmap } from './ApMindmap';
import { ApWbsTree } from './ApWbsTree';
import { useProject, useProjectMilestones } from '../../../api/hooks/useProjects';
import { MilestoneFormDialog } from './MilestoneFormDialog';
import { interleaveMilestones } from '../lib/interleaveMilestones';
import { useAutosave } from '../../../hooks/useAutosave';
import { AutosaveIndicator } from '../../../components/common/AutosaveIndicator';
import { formatApiError, ApiError } from '../../../api/client';
import { PROJECT_PERMISSIONS } from '@pm/shared';
import { usePermissionContext } from '../../../context/PermissionContext';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { buildArbeitspaketeReport } from '../reports/arbeitspaketeReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { projectKuerzel, nextTopLevelNr, nextChildNr, apDepth, computeRenumbering, reparentPlan, apParentIds, collapsedHiddenIds, type ApNumberNode } from '../lib/apNumbering';
import { AppDialogTitle } from '../../../components/common/AppDialogTitle';
import { progressForStatus, displayProgress, AP_DONE_STATUS } from '../lib/apStatus';

interface MilestoneItem {
  id: string;
  name: string;
  deadline: string | null;
  [key: string]: unknown;
}

interface Arbeitspaket {
  id: string;
  nr: string;
  parent_id: string | null;
  titel: string | null;
  beschreibung: string | null;
  leistungsbeschreibung: string | null;
  dod: string | null;
  start_date: string | null;
  end_date: string | null;
  story_points: number | null;
  // T-Shirt-Größe (P-19) — ergänzend zu story_points, optional/nullable.
  groesse: Groesse | null;
  status: 'Offen' | 'In Arbeit' | 'Erledigt' | 'Blockiert';
  progress: number;
  is_milestone: boolean;
  // RACI als Freitext (Personen frei eintragbar).
  raci_r: string | null;
  raci_a: string | null;
  raci_c: string | null;
  raci_i: string | null;
  /** PHQ-86: nur in der Liste mit include_deleted gesetzt. */
  deleted_at?: string | null;
}

// Alle RACI-Spalten starten ausgeblendet (PHQ-74) und lassen sich über den Spalten-Picker einblenden. Bereits gespeicherte
// Nutzer-Auswahlen (localStorage) überschreiben diesen Default — siehe
// useTableSortFilter (`defaultHidden` greift nur ohne Persistenz).
const RACI_FIELDS = [
  { key: 'raci_r', label: 'R', long: 'Responsible', defaultHidden: true },
  { key: 'raci_a', label: 'A', long: 'Accountable', defaultHidden: true },
  { key: 'raci_c', label: 'C', long: 'Consulted', defaultHidden: true },
  { key: 'raci_i', label: 'I', long: 'Informed', defaultHidden: true },
] as const;

const STATUSES = ['Offen', 'In Arbeit', 'Erledigt', 'Blockiert'] as const;

// T-Shirt-Größe (P-19): S/M/L/XL/XXL, optional (leerer Wert = nicht gesetzt).
type Groesse = 'S' | 'M' | 'L' | 'XL' | 'XXL';
const GROESSEN: Groesse[] = ['S', 'M', 'L', 'XL', 'XXL'];

type ApView = 'table' | 'graph' | 'mindmap' | 'wbs';

/** PHQ-107: Mindmap und Abhängigkeiten vorerst ausgeblendet (Code bleibt erhalten). */
const HIDDEN_AP_VIEWS: readonly ApView[] = ['mindmap', 'graph'];

/** Person aus dem Projekt-Team (Name + Rolle) als Vorschlag für die RACI-Felder (PHQ-75). */
interface TeamPerson { name: string; rolle: string | null }

/**
 * RACI-Eingabe: Dropdown mit den Team-Personen (Name + Rolle), freie Eingabe neuer
 * Namen bleibt möglich (freeSolo). Gespeichert wird weiterhin nur der Name als Text.
 */
function PersonField({
  value, onChange, onKeyDown, options, label, variant = 'standard',
}: {
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  options: TeamPerson[];
  label?: string;
  variant?: 'standard' | 'outlined';
}) {
  return (
    <Autocomplete<TeamPerson, false, false, true>
      freeSolo
      fullWidth
      options={options}
      value={value || null}
      inputValue={value}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
      isOptionEqualToValue={(o, v) => o.name === (typeof v === 'string' ? v : v.name)}
      onInputChange={(_, v) => onChange(v)}
      onChange={(_, v) => onChange(typeof v === 'string' ? v : (v?.name ?? ''))}
      renderOption={(props, o) => {
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
        return (
          <li key={key} {...rest}>
            {o.name}{o.rolle ? <Box component="span" sx={{ ml: 1, color: 'var(--pm-fg-subtle)', fontSize: '0.8em' }}>{o.rolle}</Box> : null}
          </li>
        );
      }}
      renderInput={(params) => <TextField {...params} variant={variant} label={label} onKeyDown={onKeyDown} />}
    />
  );
}

export function ArbeitspaketeListe({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();

  const { data, isLoading } = useArbeitspakete(projectId);
  const { data: projectResp } = useProject(projectId);
  const { data: teamResp } = useProjectTeam(projectId);
  // PHQ-101: Meilensteine farblich abgesetzt zwischen den APs; Bearbeiten über den Meilenstein-Dialog.
  const { data: msResp } = useProjectMilestones(projectId);
  const milestones = useMemo(
    () => ((msResp as unknown as { data?: MilestoneItem[] } | undefined)?.data ?? [])
      .slice().sort((a, b) => (a.deadline ?? '9999').localeCompare(b.deadline ?? '9999')),
    [msResp],
  );
  const [msEdit, setMsEdit] = useState<MilestoneItem | null>(null);
  // Team-Personen als RACI-Vorschläge (je Name einmal, mit Rolle).
  const teamPersons = useMemo<TeamPerson[]>(() => {
    const raw = (teamResp as unknown as { data?: Array<{ name?: string | null; rolle?: string | null }> } | undefined)?.data ?? [];
    const seen = new Set<string>();
    const out: TeamPerson[] = [];
    for (const m of raw) {
      const name = (m.name ?? '').trim();
      if (!name || seen.has(name)) continue;
      seen.add(name);
      out.push({ name, rolle: m.rolle ?? null });
    }
    return out;
  }, [teamResp]);
  const createMut = useCreateArbeitspaket(projectId);
  const updateMut = useUpdateArbeitspaket(projectId);
  const deleteMut = useDeleteArbeitspaket(projectId);
  const restoreMut = useRestoreArbeitspaket(projectId);
  // PHQ-86: gelöschte APs nur mit Recht arbeitspaket.delete (Liste + Wiederherstellen).
  const { hasPermission } = usePermissionContext();
  const canSeeDeleted = hasPermission(PROJECT_PERMISSIONS.ARBEITSPAKET.DELETE);
  const [showDeleted, setShowDeleted] = useState(false);
  const { data: deletedResp } = useDeletedArbeitspakete(projectId, canSeeDeleted && showDeleted);
  const deletedItems = useMemo(
    () => (((deletedResp as unknown as { data?: Arbeitspaket[] } | undefined)?.data ?? []).filter((a) => !!a.deleted_at)),
    [deletedResp],
  );
  const items = ((data as unknown as { data?: Arbeitspaket[] } | undefined)?.data ?? []);
  useSyncMaturityFromCount(projectId, 10, isLoading ? -1 : items.length);
  const projectName = ((projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name) ?? 'Projekt';
  const projectNickname = ((projectResp as unknown as { data?: { nickname?: string | null } } | undefined)?.data?.nickname) ?? null;
  // Kürzel für die Auto-Nummerierung (Spitzname → Initialen → 'AP').
  const kuerzel = projectKuerzel(projectNickname, projectName);
  const apNodes = useMemo<ApNumberNode[]>(() => items.map((a) => ({ id: a.id, nr: a.nr, parent_id: a.parent_id })), [items]);
  const depthById = useMemo(() => {
    const byId = new Map(apNodes.map((n) => [n.id, n]));
    const m = new Map<string, number>();
    apNodes.forEach((n) => m.set(n.id, apDepth(n, byId)));
    return m;
  }, [apNodes]);

  // Hierarchie auf-/zuklappen: zugeklappte APs blenden ihre Unterpunkte aus.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const parentIds = useMemo(() => apParentIds(apNodes), [apNodes]);
  const hiddenIds = useMemo(() => collapsedHiddenIds(apNodes, collapsed), [apNodes, collapsed]);
  const toggleCollapse = useCallback((id: string) => {
    setCollapsed((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);

  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState<Arbeitspaket | null>(null);
  // Beim Anlegen eines Unter-APs: das gewählte Eltern-AP (null = oberste Ebene).
  const [parentForNew, setParentForNew] = useState<Arbeitspaket | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [view, setView] = useState<ApView>('table');

  const columns = useMemo<SortFilterColumn<Arbeitspaket>[]>(() => [
    { key: 'nr', label: t('arbeitspakete.nr', 'Nr.'), hideable: false, value: (r) => r.nr },
    {
      key: 'titel', label: t('arbeitspakete.titel', 'Titel'), hideable: false,
      filter: { type: 'text' }, value: (r) => r.titel ?? '',
    },
    { key: 'zeitraum', label: t('arbeitspakete.zeitraum', 'Zeitraum'), value: (r) => r.start_date ?? null },
    { key: 'story_points', label: t('arbeitspakete.storyPoints', 'SP'), align: 'right', defaultHidden: true, value: (r) => r.story_points ?? null },
    {
      key: 'groesse', label: t('arbeitspakete.groesse', 'Größe'),
      filter: { type: 'enum', options: GROESSEN.map((g) => ({ value: g, label: g })) },
      defaultHidden: true, value: (r) => r.groesse ?? '',
    },
    { key: 'progress', label: t('arbeitspakete.progress', 'Fortschritt'), defaultHidden: true, value: (r) => r.progress },
    {
      key: 'status', label: t('fields.state', 'Status'),
      filter: { type: 'enum', options: STATUSES.map((s) => ({ value: s, label: s })) },
      value: (r) => r.status,
    },
    {
      key: 'leistungsbeschreibung', label: t('arbeitspakete.leistungsbeschreibung', 'Leistungsbeschreibung'),
      filter: { type: 'text' }, defaultHidden: true, value: (r) => r.leistungsbeschreibung ?? '',
    },
    {
      key: 'dod', label: t('arbeitspakete.dod', 'Definition of Done'),
      filter: { type: 'text' }, defaultHidden: true, value: (r) => r.dod ?? '',
    },
    ...RACI_FIELDS.map((f) => ({
      key: f.key,
      label: t(`arbeitspakete.${f.key}`, f.label),
      filter: { type: 'text' as const },
      defaultHidden: f.defaultHidden,
      value: (r: Arbeitspaket) => r[f.key] ?? '',
    })),
  ], [t]);

  const sf = useTableSortFilter(items, columns, { tableId: 'arbeitspakete' });

  const [form, setForm] = useState<{
    nr: string; titel: string; beschreibung: string;
    leistungsbeschreibung: string; dod: string;
    start_date: Dayjs | null; end_date: Dayjs | null;
    story_points: string; groesse: Groesse | ''; status: Arbeitspaket['status'];
    progress: number; is_milestone: boolean;
    raci_r: string; raci_a: string; raci_c: string; raci_i: string;
  }>({
    nr: '', titel: '', beschreibung: '', leistungsbeschreibung: '', dod: '',
    start_date: null, end_date: null,
    story_points: '', groesse: '', status: 'Offen', progress: 0, is_milestone: false,
    raci_r: '', raci_a: '', raci_c: '', raci_i: '',
  });

  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setForm({
        nr: editItem.nr, titel: editItem.titel ?? '', beschreibung: editItem.beschreibung ?? '',
        leistungsbeschreibung: editItem.leistungsbeschreibung ?? '', dod: editItem.dod ?? '',
        start_date: editItem.start_date ? dayjs(editItem.start_date) : null,
        end_date: editItem.end_date ? dayjs(editItem.end_date) : null,
        story_points: editItem.story_points != null ? String(editItem.story_points) : '',
        groesse: editItem.groesse ?? '',
        status: editItem.status, progress: editItem.progress, is_milestone: editItem.is_milestone,
        raci_r: editItem.raci_r ?? '', raci_a: editItem.raci_a ?? '',
        raci_c: editItem.raci_c ?? '', raci_i: editItem.raci_i ?? '',
      });
    } else {
      // Auto-Nr: Unter-AP = <Eltern-Nr>.<M>, sonst Top-Level = <Kürzel>-AP-<N>.
      const autoNr = parentForNew
        ? nextChildNr({ id: parentForNew.id, nr: parentForNew.nr, parent_id: parentForNew.parent_id }, apNodes)
        : nextTopLevelNr(kuerzel, apNodes);
      setForm({ nr: autoNr, titel: '', beschreibung: '', leistungsbeschreibung: '', dod: '', start_date: null, end_date: null, story_points: '', groesse: '', status: 'Offen', progress: 0, is_milestone: false, raci_r: '', raci_a: '', raci_c: '', raci_i: '' });
    }
  }, [open, editItem, parentForNew, kuerzel, apNodes]);

  const handleSave = async () => {
    if (!form.nr.trim() || !form.titel.trim()) {
      notify(tc('errors.requiredFields', 'Pflichtfelder ausfüllen (Nr. + Titel)'), 'warning');
      return;
    }
    const payload: Record<string, unknown> = {
      nr: form.nr.trim(),
      titel: form.titel.trim(),
      beschreibung: form.beschreibung.trim() || null,
      leistungsbeschreibung: form.leistungsbeschreibung.trim() || null,
      dod: form.dod.trim() || null,
      start_date: form.start_date?.format('YYYY-MM-DD') ?? null,
      end_date: form.end_date?.format('YYYY-MM-DD') ?? null,
      story_points: form.story_points ? Number(form.story_points) : null,
      groesse: form.groesse || null,
      status: form.status,
      progress: progressForStatus(form.status, editItem?.status ?? null, form.progress),
      is_milestone: form.is_milestone,
      raci_r: form.raci_r.trim() || null,
      raci_a: form.raci_a.trim() || null,
      raci_c: form.raci_c.trim() || null,
      raci_i: form.raci_i.trim() || null,
    };
    try {
      if (editItem) await updateMut.mutateAsync({ id: editItem.id, data: payload });
      else await createMut.mutateAsync({ ...payload, parent_id: parentForNew?.id ?? null });
      notify(tc('status.saved', 'Gespeichert'), 'success');
      setOpen(false);
      setEditItem(null);
      setParentForNew(null);
    } catch (err) { notify(formatApiError(err), 'error'); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await deleteMut.mutateAsync(deleteId); notify(tc('status.deleted', 'Gelöscht'), 'success'); }
    catch (err) { notify(formatApiError(err), 'error'); }
    finally { setDeleteId(null); }
  };

  // „Alle aufs neue Schema": bestehende WBS-Nummern auf <Kürzel>-AP-… umstellen
  // und parent_id aus der WBS-Hierarchie ableiten. Button nur sichtbar, wenn nötig.
  const renumberPlan = useMemo(() => computeRenumbering(apNodes, kuerzel), [apNodes, kuerzel]);
  const [renumbering, setRenumbering] = useState(false);
  const handleRenumber = async () => {
    if (renumberPlan.length === 0 || renumbering) return;
    setRenumbering(true);
    try {
      // Eltern vor Kindern patchen (Nummern hängen nicht voneinander ab, aber
      // so bleibt der Zustand zwischendurch konsistent).
      for (const p of renumberPlan) {
        await updateMut.mutateAsync({ id: p.id, data: { nr: p.nr, parent_id: p.parent_id } });
      }
      notify(t('arbeitspakete.renumberDone', 'Nummerierung umgestellt ({{n}} Pakete).', { n: renumberPlan.length }), 'success');
    } catch (err) { notify(formatApiError(err), 'error'); }
    finally { setRenumbering(false); }
  };

  // WBS-Diagramm: AP umhängen (parent_id ändern) inkl. Neunummerierung des Teilbaums.
  const handleReparent = async (childId: string, newParentId: string | null) => {
    const plan = reparentPlan(apNodes, childId, newParentId, kuerzel);
    if (plan.length === 0) {
      notify(t('arbeitspakete.reparentInvalid', 'Umhängen nicht möglich (würde einen Zyklus erzeugen).'), 'warning');
      return;
    }
    try {
      for (const p of plan) {
        await updateMut.mutateAsync({ id: p.id, data: { nr: p.nr, parent_id: p.parent_id } });
      }
      notify(t('arbeitspakete.reparentDone', 'Umgehängt ({{n}} Pakete neu nummeriert).', { n: plan.length }), 'success');
    } catch (err) { notify(formatApiError(err), 'error'); }
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  // PHQ-86: Lösch-Bestätigung nennt die Folgen. Das Backend löscht weich; über „Gelöschte anzeigen"
  // lässt sich das AP wiederherstellen. Kanban-Boards/-Karten bleiben in der DB und sind nach dem
  // Wiederherstellen wieder erreichbar; Unter-APs bleiben bestehen.
  const handleRestore = async (ap: Arbeitspaket) => {
    try {
      await restoreMut.mutateAsync(ap.id);
      notify(t('arbeitspakete.restoreDone', 'Arbeitspaket {{nr}} wiederhergestellt.', { nr: ap.nr }), 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Der Client übersetzt den Code CONFLICT allgemein; die Ursache lässt sich aus der Liste ableiten.
        const parentGone = !!ap.parent_id && !items.some((a) => a.id === ap.parent_id);
        const msg = items.some((a) => a.nr === ap.nr)
          ? t('arbeitspakete.restoreNrTaken', 'Nummer {{nr}} ist inzwischen vergeben.', { nr: ap.nr })
          : parentGone
            ? t('arbeitspakete.restoreParentDeleted', 'Übergeordnetes Arbeitspaket ist gelöscht.')
            : t('arbeitspakete.restoreConflict', 'Wiederherstellen nicht möglich (Nummer vergeben, übergeordnetes Arbeitspaket gelöscht oder Arbeitspaket nicht gelöscht).');
        notify(msg, 'error');
      } else {
        notify(formatApiError(err), 'error');
      }
    }
  };

  const deleteChildren = deleteId ? items.filter((a) => a.parent_id === deleteId).length : 0;
  const deleteMessage = [
    t('arbeitspakete.deleteConsequences', 'Dieses Arbeitspaket wird gelöscht und verschwindet aus Liste, Gantt-Chart und Kanban. Das zugehörige Kanban-Board mit allen Karten bleibt erhalten. Über „Gelöschte anzeigen" lässt es sich wiederherstellen.'),
    deleteChildren > 0
      ? t('arbeitspakete.deleteChildren', 'Die {{n}} Unter-Arbeitspakete werden nicht mitgelöscht.', { n: deleteChildren })
      : '',
  ].filter(Boolean).join(' ');

  const pdfMeta: ReportMeta = { projectName, pageTitle: t('arbeitspakete.title', 'Arbeitspakete') };
  const buildReport = () => buildArbeitspaketeReport({ items });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">{t('arbeitspakete.title', 'Arbeitspakete')}</Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {view === 'table' && renumberPlan.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AccountTreeOutlinedIcon />}
                onClick={handleRenumber}
                disabled={renumbering}
              >
                {t('arbeitspakete.renumber', 'Auf {{k}}-Schema umstellen', { k: kuerzel })}
              </Button>
            )}
            {view === 'table' && canSeeDeleted && (
              <FormControlLabel
                sx={{ m: 0 }}
                control={<Switch size="small" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />}
                label={t('arbeitspakete.showDeleted', 'Gelöschte anzeigen')}
              />
            )}
            {view === 'table' && (
              <PmColumnPicker columns={sf.hideableColumns} isVisible={sf.isVisible} onToggle={sf.toggleColumn} />
            )}
            <PdfExportButton meta={pdfMeta} build={buildReport} />
            <PmViewToggle<ApView>
              value={view}
              onChange={setView}
              options={([
                { value: 'table', label: t('arbeitspakete.viewTable', 'Tabelle'), icon: <TableRowsIcon fontSize="small" /> },
                { value: 'wbs', label: t('arbeitspakete.viewWbs', 'WBS-Diagramm'), icon: <SchemaIcon fontSize="small" /> },
                { value: 'mindmap', label: t('arbeitspakete.viewMindmap', 'Mindmap'), icon: <HubIcon fontSize="small" /> },
                { value: 'graph', label: t('arbeitspakete.viewGraph', 'Abhängigkeiten'), icon: <AccountTreeIcon fontSize="small" /> },
              ] as { value: ApView; label: string; icon: ReactElement }[]).filter((o) => !HIDDEN_AP_VIEWS.includes(o.value))}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditItem(null); setParentForNew(null); setOpen(true); }}>
              {t('arbeitspakete.add', 'Neues AP')}
            </Button>
          </Stack>
        </Stack>

        {view === 'graph' ? (
          <ApDependencyEditor projectId={projectId} />
        ) : view === 'mindmap' ? (
          <PmCard>
            <ApMindmap projectName={projectName} items={items} />
          </PmCard>
        ) : view === 'wbs' ? (
          <PmCard>
            <ApWbsTree projectName={projectName} items={items} onReparent={handleReparent} />
          </PmCard>
        ) : (
          <PmCard flush>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--pm-bg-muted)' }}>
              <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
                {t('arbeitspakete.inlineHint', 'Direkt in der Zeile bearbeiten (Enter speichert). Spalten über „Spalten" ein-/ausblenden; für alle Felder gebündelt das Stift-Symbol → große Maske.')}
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {sf.visibleColumns.map((col) => (
                    <PmHeadCell
                      key={col.key}
                      label={col.label}
                      align={col.align}
                      filter={col.filter}
                      sortActive={sf.sortKey === col.key}
                      sortDir={sf.sortDir}
                      onToggleSort={() => sf.toggleSort(col.key)}
                      filterValue={sf.filters[col.key]}
                      onSetFilter={(v) => sf.setFilter(col.key, v)}
                      onClearFilter={() => sf.clearFilter(col.key)}
                      headSx={col.key === 'titel'
                        // Titel-Spalte optisch prominenter (Leitspalte der Liste).
                        ? { color: 'var(--pm-fg-base)', fontSize: '0.85rem' }
                        : { color: 'var(--pm-fg-subtle)', fontWeight: 600 }}
                      labelSx={col.key === 'titel' ? { fontWeight: 700 } : undefined}
                      minWidth={col.key === 'progress' ? 160 : undefined}
                    />
                  ))}
                  <TableCell sx={{ color: 'var(--pm-fg-subtle)', fontWeight: 600 }} align="right">{tc('actions.actions', 'Aktionen')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <AddApRow isVisible={sf.isVisible} personOptions={teamPersons} autoNr={nextTopLevelNr(kuerzel, apNodes)} onCreate={async (fields) => {
                  await createMut.mutateAsync({
                    nr: fields.nr,
                    parent_id: null,
                    titel: fields.titel.trim(),
                    beschreibung: null,
                    leistungsbeschreibung: fields.leistungsbeschreibung.trim() || null,
                    dod: fields.dod.trim() || null,
                    start_date: null,
                    end_date: null,
                    story_points: fields.story_points ? Number(fields.story_points) : null,
                    groesse: fields.groesse || null,
                    status: fields.status,
                    progress: 0,
                    is_milestone: false,
                    raci_r: fields.raci_r.trim() || null,
                    raci_a: fields.raci_a.trim() || null,
                    raci_c: fields.raci_c.trim() || null,
                    raci_i: fields.raci_i.trim() || null,
                  });
                }} />
                {sf.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={sf.visibleColumns.length + 1}>
                      <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
                        {items.length === 0
                          ? t('arbeitspakete.emptyHint', 'Lege das erste Arbeitspaket an um die Projekt-Struktur aufzubauen.')
                          : t('arbeitspakete.filterEmpty', 'Keine Treffer für die aktuelle Filter-Kombination.')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {interleaveMilestones(sf.rows.filter((ap) => !hiddenIds.has(ap.id)), milestones).map((node) => {
                  if (node.kind === 'ms') {
                    return (
                      <MilestoneListRow key={`ms-${node.ms.id}`} ms={node.ms} colSpan={Math.max(sf.visibleColumns.length, 1)} onEdit={() => setMsEdit(node.ms)} />
                    );
                  }
                  const ap = node.ap;
                  return (
                  <ApRow
                    key={ap.id}
                    ap={ap}
                    depth={depthById.get(ap.id) ?? 0}
                    hasChildren={parentIds.has(ap.id)}
                    collapsed={collapsed.has(ap.id)}
                    onToggleCollapse={() => toggleCollapse(ap.id)}
                    isVisible={sf.isVisible}
                    personOptions={teamPersons}
                    onToggleDone={async (done) => {
                      const status = done ? AP_DONE_STATUS : 'In Arbeit';
                      try {
                        await updateMut.mutateAsync({
                          id: ap.id,
                          data: { status, progress: progressForStatus(status, ap.status, ap.progress) },
                        });
                      } catch (err) { notify(formatApiError(err), 'error'); }
                    }}
                    onAddChild={() => { setEditItem(null); setParentForNew(ap); setOpen(true); }}
                    onSave={async (core) => {
                      await updateMut.mutateAsync({
                        id: ap.id,
                        data: {
                          nr: ap.nr,
                          titel: core.titel.trim(),
                          beschreibung: ap.beschreibung,
                          leistungsbeschreibung: core.leistungsbeschreibung.trim() || null,
                          dod: core.dod.trim() || null,
                          start_date: ap.start_date,
                          end_date: ap.end_date,
                          story_points: core.story_points ? Number(core.story_points) : null,
                          groesse: core.groesse || null,
                          status: core.status,
                          progress: progressForStatus(core.status, ap.status, ap.progress),
                          is_milestone: ap.is_milestone,
                          raci_r: core.raci_r.trim() || null,
                          raci_a: core.raci_a.trim() || null,
                          raci_c: core.raci_c.trim() || null,
                          raci_i: core.raci_i.trim() || null,
                        },
                      });
                    }}
                    onEditDetail={() => { setParentForNew(null); setEditItem(ap); setOpen(true); }}
                    onDelete={() => setDeleteId(ap.id)}
                  />
                
                  );
                })}
                {showDeleted && canSeeDeleted && deletedItems.map((ap) => (
                  <DeletedApRow
                    key={ap.id}
                    ap={ap}
                    colSpan={Math.max(sf.visibleColumns.length - 2, 1)}
                    busy={restoreMut.isPending}
                    onRestore={() => void handleRestore(ap)}
                  />
                ))}
              </TableBody>
            </Table>
          </PmCard>
        )}

        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
          <AppDialogTitle>
            {editItem
              ? t('arbeitspakete.editTitle', 'AP bearbeiten')
              : parentForNew
                ? t('arbeitspakete.createChildTitle', 'Neues Unter-Arbeitspaket')
                : t('arbeitspakete.createTitle', 'Neues AP')}
          </AppDialogTitle>
          <DialogContent>
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              {!editItem && parentForNew && (
                <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
                  {t('arbeitspakete.childOf', 'Unterpunkt von')} <strong>{parentForNew.nr}</strong>{parentForNew.titel ? ` — ${parentForNew.titel}` : ''}
                </Typography>
              )}
              <TextField
                label={t('arbeitspakete.nr', 'Nr.')}
                value={form.nr}
                InputProps={{ readOnly: true }}
                helperText={t('arbeitspakete.nrAuto', 'Wird automatisch aus Projekt-Kürzel und Hierarchie vergeben.')}
                fullWidth
              />
              <TextField required label={t('arbeitspakete.titel', 'Titel')} value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} fullWidth />
              <TextField label={t('fields.description', 'Beschreibung')} value={form.beschreibung} onChange={(e) => setForm({ ...form, beschreibung: e.target.value })} multiline rows={2} fullWidth />
              <TextField label={t('arbeitspakete.leistungsbeschreibung', 'Leistungsbeschreibung')} value={form.leistungsbeschreibung} onChange={(e) => setForm({ ...form, leistungsbeschreibung: e.target.value })} multiline rows={3} fullWidth />
              <TextField label={t('arbeitspakete.dod', 'Definition of Done')} value={form.dod} onChange={(e) => setForm({ ...form, dod: e.target.value })} multiline rows={3} fullWidth />
              <DatePicker label={t('arbeitspakete.startDate', 'Start')} value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} slotProps={{ textField: { fullWidth: true } }} />
              <DatePicker label={t('arbeitspakete.endDate', 'Ende')} value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} slotProps={{ textField: { fullWidth: true } }} />
              <TextField label={t('arbeitspakete.storyPoints', 'Story Points')} value={form.story_points} onChange={(e) => setForm({ ...form, story_points: e.target.value.replace(/\D/g, '') })} type="number" inputProps={{ min: 0 }} fullWidth />
              <FormControl fullWidth>
                <InputLabel>{t('arbeitspakete.groesse', 'Größe')}</InputLabel>
                <Select
                  value={form.groesse}
                  label={t('arbeitspakete.groesse', 'Größe')}
                  onChange={(e) => setForm({ ...form, groesse: e.target.value as Groesse | '' })}
                >
                  <MenuItem value="">—</MenuItem>
                  {GROESSEN.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t('fields.state', 'Status')}</InputLabel>
                <Select value={form.status} label={t('fields.state', 'Status')} onChange={(e) => {
                  const status = e.target.value as Arbeitspaket['status'];
                  setForm({ ...form, status, progress: progressForStatus(status, form.status, form.progress) });
                }}>
                  {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                label={t('arbeitspakete.progress', 'Fortschritt %')}
                value={form.progress}
                onChange={(e) => setForm({ ...form, progress: Math.min(100, Math.max(0, Number(e.target.value.replace(/\D/g, '')) || 0)) })}
                type="number" inputProps={{ min: 0, max: 100 }} fullWidth
              />
              <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', fontWeight: 600, mt: 0.5 }}>
                {t('arbeitspakete.raciSection', 'RACI (Freitext)')}
              </Typography>
              {RACI_FIELDS.map((f) => (
                <PersonField
                  key={f.key}
                  variant="outlined"
                  label={`${f.label} — ${t(`arbeitspakete.${f.key}Long`, f.long)}`}
                  value={form[f.key]}
                  onChange={(v) => setForm((prev) => ({ ...prev, [f.key]: v }))}
                  options={teamPersons}
                />
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>{tc('actions.cancel', 'Abbrechen')}</Button>
            <Button variant="contained" onClick={handleSave} disabled={!form.nr.trim() || !form.titel.trim() || createMut.isPending || updateMut.isPending}>
              {tc('actions.save', 'Speichern')}
            </Button>
          </DialogActions>
        </Dialog>

        <MilestoneFormDialog
          open={!!msEdit}
          mode="edit"
          projectId={projectId}
          initialData={msEdit ?? undefined}
          onClose={() => setMsEdit(null)}
        />
        <ConfirmDialog
          open={!!deleteId}
          title={t('arbeitspakete.deleteTitle', 'AP löschen?')}
          message={deleteMessage}
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      </Box>
    </LocalizationProvider>
  );
}

/** PHQ-86: gelöschtes AP — ausgegraut, nur lesbar, mit „Wiederherstellen". */
function MilestoneListRow({ ms, colSpan, onEdit }: { ms: MilestoneItem; colSpan: number; onEdit: () => void }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  return (
    <TableRow data-testid={`ms-row-${ms.id}`} sx={{ bgcolor: 'color-mix(in srgb, var(--pm-warning) 16%, transparent)' }}>
      <TableCell colSpan={colSpan}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box component="span" sx={{ color: 'var(--pm-warning)', fontSize: '1rem', lineHeight: 1 }}>◊</Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{ms.name}</Typography>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
            {t('milestones.title', 'Meilenstein')}{ms.deadline ? ` · ${new Date(ms.deadline).toLocaleDateString('de-DE')}` : ''}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="right">
        <IconButton size="small" aria-label={tc('actions.edit', 'Bearbeiten')} onClick={onEdit}>
          <EditIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

function DeletedApRow({
  ap, colSpan, busy, onRestore,
}: { ap: Arbeitspaket; colSpan: number; busy: boolean; onRestore: () => void }) {
  const { t } = useTranslation('projects');
  return (
    <TableRow data-testid={`ap-deleted-${ap.id}`} sx={{ bgcolor: 'var(--pm-bg-subtle)', '& > td:not(:last-child)': { opacity: 0.55 } }}>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{ap.nr}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>{ap.titel}</Typography>
      </TableCell>
      <TableCell colSpan={colSpan}>
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
          {t('arbeitspakete.deletedAt', 'Gelöscht')}{ap.deleted_at ? ` ${dayjs(ap.deleted_at).format('DD.MM.YYYY')}` : ''}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Button size="small" startIcon={<RestoreFromTrashIcon />} onClick={onRestore} disabled={busy}>
          {t('arbeitspakete.restore', 'Wiederherstellen')}
        </Button>
      </TableCell>
    </TableRow>
  );
}

/**
 * Inline-Anlegen direkt in der Tabelle: pro EINGEBLENDETER Spalte ein Eingabefeld
 * (Status/RACI/SP/Leistungsbeschreibung/DoD inklusive). Enter springt ins naechste
 * Feld der Zeile; im letzten Feld legt Enter an. Nr. + Titel sind Pflicht.
 * Zeitraum/Fortschritt werden nach dem Anlegen ueber den Stift gesetzt (nicht
 * inline editierbar) — ihre Zellen bleiben in der Anlege-Zeile leer.
 */
function AddApRow({
  isVisible, autoNr, onCreate, personOptions,
}: {
  isVisible: (key: string) => boolean;
  personOptions: TeamPerson[];
  autoNr: string;
  onCreate: (fields: ApInlineFields) => Promise<void>;
}) {
  const { t } = useTranslation('projects');
  const EMPTY: ApInlineFields = {
    nr: '', titel: '', story_points: '', groesse: '', status: 'Offen',
    leistungsbeschreibung: '', dod: '', raci_r: '', raci_a: '', raci_c: '', raci_i: '',
  };
  const [form, setForm] = useState<ApInlineFields>(EMPTY);
  const [busy, setBusy] = useState(false);
  // Nr. ist auto-vergeben → nur der Titel ist hier Pflicht.
  const canCreate = form.titel.trim().length > 0;
  const set = (patch: Partial<ApInlineFields>) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    if (!canCreate || busy) return;
    setBusy(true);
    try { await onCreate({ ...form, nr: autoNr }); setForm(EMPTY); } finally { setBusy(false); }
  };

  // Enter: naechstes Feld DER ZEILE; im letzten Feld -> anlegen.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    const cur = e.target as HTMLElement;
    const row = cur.closest('tr');
    const inputs = row
      ? Array.from(row.querySelectorAll<HTMLElement>('input:not([type="hidden"]):not([aria-hidden="true"]), .MuiSelect-select')).filter((el) => el.getAttribute('tabindex') !== '-1')
      : [];
    const next = inputs[inputs.indexOf(cur) + 1];
    if (next) { next.focus(); if (next instanceof HTMLInputElement) next.select(); return; }
    void submit();
  };

  return (
    <TableRow sx={{ bgcolor: 'var(--pm-bg-subtle)' }}>
      {isVisible('nr') && (
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'var(--pm-fg-muted)', whiteSpace: 'nowrap' }} title={t('arbeitspakete.nrAuto', 'Wird automatisch vergeben.')}>{autoNr}</Typography>
        </TableCell>
      )}
      {isVisible('titel') && (
        <TableCell>
          <TextField
            variant="standard"
            fullWidth
            autoFocus
            placeholder={t('arbeitspakete.titelPlaceholder', 'Arbeitspaket-Titel eingeben…')}
            value={form.titel}
            onChange={(e) => set({ titel: e.target.value })}
            onKeyDown={onKeyDown}
            sx={{ '& .MuiInputBase-input': { fontSize: '1rem', fontWeight: 700 } }}
          />
        </TableCell>
      )}
      {isVisible('zeitraum') && <TableCell />}
      {isVisible('story_points') && (
        <TableCell align="right">
          <TextField variant="standard" sx={{ width: 56 }} placeholder={t('arbeitspakete.storyPoints', 'SP')} value={form.story_points} onChange={(e) => set({ story_points: e.target.value.replace(/\D/g, '') })} onKeyDown={onKeyDown} inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }} />
        </TableCell>
      )}
      {isVisible('groesse') && (
        <TableCell>
          <Select variant="standard" displayEmpty sx={{ minWidth: 56 }} value={form.groesse} onChange={(e) => set({ groesse: e.target.value as Groesse | '' })}>
            <MenuItem value="">—</MenuItem>
            {GROESSEN.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </Select>
        </TableCell>
      )}
      {isVisible('progress') && <TableCell />}
      {isVisible('status') && (
        <TableCell>
          <Select variant="standard" value={form.status} onChange={(e) => set({ status: e.target.value as Arbeitspaket['status'] })}>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </TableCell>
      )}
      {isVisible('leistungsbeschreibung') && (
        <TableCell sx={{ maxWidth: 200 }}>
          <TextField variant="standard" fullWidth value={form.leistungsbeschreibung} onChange={(e) => set({ leistungsbeschreibung: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      {isVisible('dod') && (
        <TableCell sx={{ maxWidth: 200 }}>
          <TextField variant="standard" fullWidth value={form.dod} onChange={(e) => set({ dod: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      {RACI_FIELDS.map((f) => (
        isVisible(f.key) ? (
          <TableCell key={f.key} sx={{ maxWidth: 120 }}>
            <PersonField value={form[f.key]} onChange={(v) => set({ [f.key]: v })} onKeyDown={onKeyDown} options={personOptions} />
          </TableCell>
        ) : null
      ))}
      <TableCell align="right">
        <IconButton color="primary" onClick={() => void submit()} disabled={!canCreate || busy} aria-label={t('arbeitspakete.add', 'Neues AP')}>
          <AddIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

interface ApInlineFields {
  nr: string;
  titel: string;
  story_points: string;
  groesse: Groesse | '';
  status: Arbeitspaket['status'];
  leistungsbeschreibung: string;
  dod: string;
  raci_r: string;
  raci_a: string;
  raci_c: string;
  raci_i: string;
}

const apToInline = (ap: Arbeitspaket): ApInlineFields => ({
  nr: ap.nr,
  titel: ap.titel ?? '',
  story_points: ap.story_points != null ? String(ap.story_points) : '',
  groesse: ap.groesse ?? '',
  status: ap.status,
  leistungsbeschreibung: ap.leistungsbeschreibung ?? '',
  dod: ap.dod ?? '',
  raci_r: ap.raci_r ?? '',
  raci_a: ap.raci_a ?? '',
  raci_c: ap.raci_c ?? '',
  raci_i: ap.raci_i ?? '',
});

/**
 * Bestehende AP-Zeile — sichtbare Kernfelder inline editierbar (Autosave, Enter
 * speichert sofort). Zeitraum & Fortschritt bleiben Anzeige; ihre Bearbeitung
 * (und alle Felder gebuendelt) laeuft ueber den Stift -> grosse Maske. Stift +
 * Loeschen bleiben in der Aktionsspalte erhalten.
 */
function ApRow({
  ap, depth, hasChildren, collapsed, onToggleCollapse, isVisible, onSave, onEditDetail, onDelete, onAddChild,
  personOptions, onToggleDone,
}: {
  personOptions: TeamPerson[];
  onToggleDone: (done: boolean) => Promise<void>;
  ap: Arbeitspaket;
  depth: number;
  hasChildren: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  isVisible: (key: string) => boolean;
  onSave: (core: ApInlineFields) => Promise<void>;
  onEditDetail: () => void;
  onDelete: () => void;
  onAddChild: () => void;
}) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');

  const [form, setForm] = useState<ApInlineFields>(() => apToInline(ap));

  const autosave = useAutosave<ApInlineFields>({
    canSave: (f) => f.nr.trim().length > 0 && f.titel.trim().length > 0,
    onSave: async (f) => { await onSave(f); },
  });

  useEffect(() => {
    if (autosave.isDirty) return;
    setForm(apToInline(ap));
  }, [autosave.isDirty, ap.nr, ap.titel, ap.story_points, ap.groesse, ap.status, ap.leistungsbeschreibung, ap.dod, ap.raci_r, ap.raci_a, ap.raci_c, ap.raci_i]);

  const update = (patch: Partial<ApInlineFields>) => { const next = { ...form, ...patch }; setForm(next); autosave.schedule(next); };
  const onKeyDown = (e: React.KeyboardEvent) => handleInlineEditKeyDown(e, autosave.flush);

  return (
    <TableRow hover>
      {isVisible('nr') && (
        <TableCell>
          {/* Nr. ist auto-vergeben (read-only); Einrückung + Chevron zeigen/steuern die Hierarchie. */}
          <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
            {/* PHQ-87: Häkchen links = AP erledigt (Status „Erledigt", Fortschritt 100 %). */}
            <Checkbox
              size="small"
              checked={ap.status === AP_DONE_STATUS}
              onChange={(e) => void onToggleDone(e.target.checked)}
              inputProps={{ 'aria-label': t('arbeitspakete.markDone', 'Als erledigt markieren') }}
              sx={{ p: 0.25, mr: 0.5 }}
            />
            {hasChildren ? (
              <Box
                component="span"
                role="button"
                aria-label={collapsed ? t('arbeitspakete.expand', 'Aufklappen') : t('arbeitspakete.collapse', 'Zuklappen')}
                onClick={onToggleCollapse}
                sx={{ cursor: 'pointer', mr: 0.5, color: 'var(--pm-fg-subtle)', fontSize: '0.8rem', lineHeight: 1, userSelect: 'none', width: 14, textAlign: 'center' }}
              >
                {collapsed ? '▸' : '▾'}
              </Box>
            ) : (
              <Box component="span" sx={{ display: 'inline-block', width: 14, mr: 0.5 }} />
            )}
            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: depth > 0 ? 'var(--pm-fg-muted)' : 'var(--pm-fg-base)' }}>
              {ap.nr}
            </Typography>
          </Box>
        </TableCell>
      )}
      {isVisible('titel') && (
        <TableCell>
          {/* PHQ-85: Titel je Hierarchie-Ebene weiter eingerückt (24 px pro Ebene). */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: depth * 3 }}>
            <TextField
              variant="standard"
              fullWidth
              value={form.titel}
              onChange={(e) => update({ titel: e.target.value })}
              onKeyDown={onKeyDown}
              sx={{ '& .MuiInputBase-input': { fontSize: '1rem', fontWeight: 700 } }}
            />
            {ap.is_milestone && <PmPill tone="warning">{t('arbeitspakete.milestone', 'Meilenstein')}</PmPill>}
          </Stack>
        </TableCell>
      )}
      {isVisible('zeitraum') && (
        <TableCell sx={{ fontSize: '0.78rem', color: 'var(--pm-fg-muted)', whiteSpace: 'nowrap' }}>
          {ap.start_date && ap.end_date
            ? `${dayjs(ap.start_date).format('DD.MM.')} – ${dayjs(ap.end_date).format('DD.MM.YYYY')}`
            : '—'}
        </TableCell>
      )}
      {isVisible('story_points') && (
        <TableCell align="right">
          <TextField variant="standard" sx={{ width: 56 }} value={form.story_points} onChange={(e) => update({ story_points: e.target.value.replace(/\D/g, '') })} onKeyDown={onKeyDown} inputProps={{ inputMode: 'numeric', style: { textAlign: 'right', fontFamily: 'monospace' } }} />
        </TableCell>
      )}
      {isVisible('groesse') && (
        <TableCell>
          <Select variant="standard" displayEmpty sx={{ minWidth: 56 }} value={form.groesse} onChange={(e) => update({ groesse: e.target.value as Groesse | '' })}>
            <MenuItem value="">—</MenuItem>
            {GROESSEN.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </Select>
        </TableCell>
      )}
      {isVisible('progress') && (
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LinearProgress
              variant="determinate"
              value={displayProgress(ap.status, ap.progress)}
              sx={{
                flex: 1, height: 5, borderRadius: 3, background: 'var(--pm-bg-deep)',
                '& .MuiLinearProgress-bar': {
                  background: displayProgress(ap.status, ap.progress) >= 100 ? 'var(--pm-success)' : ap.progress >= 50 ? 'var(--pm-info)' : 'var(--rittec-petrol)',
                },
              }}
            />
            <Typography variant="caption" sx={{ minWidth: 32, color: 'var(--pm-fg-muted)', fontFamily: 'monospace' }}>{displayProgress(ap.status, ap.progress)}%</Typography>
          </Stack>
        </TableCell>
      )}
      {isVisible('status') && (
        <TableCell>
          <Select variant="standard" value={form.status} onChange={(e) => update({ status: e.target.value as Arbeitspaket['status'] })}>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </TableCell>
      )}
      {isVisible('leistungsbeschreibung') && (
        <TableCell sx={{ maxWidth: 200 }}>
          <TextField variant="standard" fullWidth value={form.leistungsbeschreibung} onChange={(e) => update({ leistungsbeschreibung: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      {isVisible('dod') && (
        <TableCell sx={{ maxWidth: 200 }}>
          <TextField variant="standard" fullWidth value={form.dod} onChange={(e) => update({ dod: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      {RACI_FIELDS.map((f) => (
        isVisible(f.key) ? (
          <TableCell key={f.key} sx={{ maxWidth: 120 }}>
            <PersonField value={form[f.key]} onChange={(v) => update({ [f.key]: v })} onKeyDown={onKeyDown} options={personOptions} />
          </TableCell>
        ) : null
      ))}
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
          <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} compact />
          <IconButton size="small" onClick={onAddChild} aria-label={t('arbeitspakete.addChild', 'Unter-AP hinzufügen')} title={t('arbeitspakete.addChild', 'Unter-AP hinzufügen')} sx={{ color: 'var(--pm-fg-subtle)' }}>
            <AccountTreeOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onEditDetail} aria-label={tc('actions.edit', 'Bearbeiten')} sx={{ color: 'var(--pm-fg-subtle)' }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={onDelete} aria-label={tc('actions.delete', 'Löschen')}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
